import { SiteHero } from '../components/site';
import React from 'react';
import { Server, Database, Shield, Cloud, Settings } from 'lucide-react';

export const DeveloperGuide: React.FC = () => {
  return (
    <div className="io-site developer-guide-page io-docs-wide">
      <SiteHero
        centered
        title="Developer Guide"
        subtitle="Technical documentation for developers including deployment guides, MQTT configuration, and advanced integration patterns."
        primaryCta={{ label: 'API Documentation', to: '/api-documentation' }}
      />
      <div className="io-site-docs">
        <div className="io-site-docs__inner">
      <section className="content-section">
        <h2>Architecture Overview</h2>
        <p>
          The SmartHome system is built on a modern microservices architecture 
          with the following key components:
        </p>
        
        <div className="dev-guide-cards dev-guide-cards--3">
          <article className="dev-guide-card">
            <span className="dev-guide-card__icon" aria-hidden>
              <Cloud size={22} strokeWidth={1.75} />
            </span>
            <h3>AWS IoT Core</h3>
            <p>Secure MQTT messaging and device management</p>
            <ul>
              <li>Device certificates</li>
              <li>MQTT broker</li>
              <li>Device shadows</li>
              <li>Rules engine</li>
            </ul>
          </article>

          <article className="dev-guide-card">
            <span className="dev-guide-card__icon" aria-hidden>
              <Database size={22} strokeWidth={1.75} />
            </span>
            <h3>MongoDB</h3>
            <p>Document-based data storage</p>
            <ul>
              <li>Device states</li>
              <li>User management</li>
              <li>Tenant data</li>
              <li>Audit logs</li>
            </ul>
          </article>

          <article className="dev-guide-card">
            <span className="dev-guide-card__icon" aria-hidden>
              <Server size={22} strokeWidth={1.75} />
            </span>
            <h3>Node.js Backend</h3>
            <p>Express.js API server</p>
            <ul>
              <li>RESTful APIs</li>
              <li>MQTT client</li>
              <li>Authentication</li>
              <li>Business logic</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="content-section">
        <h2>Prerequisites</h2>
        <p>Before setting up the development environment, ensure you have:</p>
        <ul>
          <li>Node.js 18+ installed</li>
          <li>MongoDB instance (local or cloud)</li>
          <li>AWS IoT Core setup with certificates</li>
          <li>PM2 for process management (production)</li>
          <li>Git for version control</li>
        </ul>
      </section>

      <section className="content-section">
        <h2>Local Development Setup</h2>
        
        <h3>1. Clone and Install Dependencies</h3>
        <div className="code-block">
          <pre>{`# Clone the repository
git clone <your-repo-url>
cd IoT-Demo

# Install dependencies
npm install`}</pre>
        </div>

        <h3>2. Environment Configuration</h3>
        <p>Create a <code>.env</code> file in the project root:</p>
        <div className="code-block">
          <pre>{`# AWS IoT Core Configuration
AWS_IOT_ENDPOINT=your-iot-endpoint.iot.region.amazonaws.com
MQTT_CLIENT_ID=smart-home-backend-dev

# MongoDB Configuration
MONGODB_URI=mongodb://localhost:27017/smart-home-dev

# Server Configuration
PORT=4008
NODE_ENV=development

# Certificate paths
CERT_PATH=./certs/`}</pre>
        </div>

        <h3>3. Certificate Setup</h3>
        <p>Place your AWS IoT certificates in the <code>certs/</code> directory:</p>
        <div className="code-block">
          <pre>{`certs/
├── certificate.pem.crt
├── private.pem.key
└── AmazonRootCA1.pem`}</pre>
        </div>

        <h3>4. Start Development Server</h3>
        <div className="code-block">
          <pre>{`# Start the development server
npm start

# Or with nodemon for auto-restart
npm run dev`}</pre>
        </div>
      </section>

      <section className="content-section">
        <h2>Production Deployment</h2>
        
        <h3>EC2 Deployment Guide</h3>
        <p>For production deployment on AWS EC2:</p>

        <h4>1. Server Setup</h4>
        <div className="code-block">
          <pre>{`# Install Node.js 18
curl -fsSL https://deb.nodesource.com/setup_18.x | sudo -E bash -
sudo apt-get install -y nodejs

# Install PM2 globally
sudo npm install -g pm2

# Install Git
sudo apt-get update
sudo apt-get install -y git`}</pre>
        </div>

        <h4>2. Application Deployment</h4>
        <div className="code-block">
          <pre>{`# Clone repository
git clone <your-repo-url>
cd IoT-Demo

# Install production dependencies
npm install --production

# Create logs directory
mkdir -p logs`}</pre>
        </div>

        <h4>3. PM2 Configuration</h4>
        <p>Create <code>ecosystem.config.js</code>:</p>
        <div className="code-block">
          <pre>{`module.exports = {
  apps: [{
    name: 'iot-smarthome-backend',
    script: 'src/app.js',
    instances: 1,
    exec_mode: 'fork',
    env: {
      NODE_ENV: 'production',
      PORT: 4008
    },
    autorestart: true,
    watch: false,
    max_memory_restart: '1G',
    restart_delay: 5000,
    max_restarts: 10,
    min_uptime: '10s',
    log_file: './logs/combined.log',
    out_file: './logs/out.log',
    error_file: './logs/error.log'
  }]
};`}</pre>
        </div>

        <h4>4. Start with PM2</h4>
        <div className="code-block">
          <pre>{`# Start the application
pm2 start ecosystem.config.js --env production

# Save PM2 configuration
pm2 save

# Setup PM2 to start on boot
pm2 startup`}</pre>
        </div>
      </section>

      <section className="content-section">
        <h2>MQTT Configuration</h2>
        
        <h3>Connection Setup</h3>
        <p>The system uses AWS IoT Core for MQTT messaging:</p>
        <div className="code-block">
          <pre>{`const mqtt = require('aws-iot-device-sdk');

const device = mqtt.device({
  keyPath: './certs/private.pem.key',
  certPath: './certs/certificate.pem.crt',
  caPath: './certs/AmazonRootCA1.pem',
  clientId: process.env.MQTT_CLIENT_ID,
  host: process.env.AWS_IOT_ENDPOINT
});`}</pre>
        </div>

        <h3>Topic Structure</h3>
        <div className="code-block">
          <pre>{`# Device commands
smart-home/device/{deviceId}/command

# Device status
smart-home/device/{deviceId}/status

# Device responses
smart-home/device/{deviceId}/response

# System health
smart-home/system/health`}</pre>
        </div>

        <h3>Message Format</h3>
        <div className="code-block">
          <pre>{`{
  "deviceId": "led-bulb-001",
  "action": "turn_on",
  "parameters": {},
  "timestamp": "2024-01-15T10:30:00Z",
  "requestId": "req-123456"
}`}</pre>
        </div>
      </section>

      <section className="content-section">
        <h2>Database Schema</h2>
        
        <h3>Device Model</h3>
        <div className="code-block">
          <pre>{`{
  "_id": ObjectId,
  "deviceId": "led-bulb-001",
  "deviceType": "led_bulb",
  "tenantId": "tenant-123",
  "status": "online",
  "lastSeen": ISODate,
  "capabilities": ["brightness", "color", "power"],
  "metadata": {
    "location": "living_room",
    "room": "main_floor"
  },
  "created_at": ISODate,
  "updated_at": ISODate
}`}</pre>
        </div>

        <h3>User Model</h3>
        <div className="code-block">
          <pre>{`{
  "_id": ObjectId,
  "email": "user@example.com",
  "tenantId": "tenant-123",
  "role": "admin",
  "permissions": ["device_control", "user_management"],
  "created_at": ISODate,
  "updated_at": ISODate
}`}</pre>
        </div>
      </section>

      <section className="content-section">
        <h2>Security Best Practices</h2>
        
        <div className="dev-guide-cards dev-guide-cards--2">
          <article className="dev-guide-card">
            <span className="dev-guide-card__icon" aria-hidden>
              <Shield size={22} strokeWidth={1.75} />
            </span>
            <h3>Authentication</h3>
            <ul>
              <li>JWT token-based authentication</li>
              <li>Role-based access control</li>
              <li>Token expiration and refresh</li>
              <li>Secure password policies</li>
            </ul>
          </article>

          <article className="dev-guide-card">
            <span className="dev-guide-card__icon" aria-hidden>
              <Settings size={22} strokeWidth={1.75} />
            </span>
            <h3>Network Security</h3>
            <ul>
              <li>TLS/SSL encryption</li>
              <li>Certificate-based device auth</li>
              <li>Firewall configuration</li>
              <li>VPN for remote access</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="content-section">
        <h2>Monitoring and Logging</h2>
        
        <h3>Health Endpoints</h3>
        <div className="code-block">
          <pre>{`# Application health
GET /health

# MQTT connection health
GET /health/mqtt

# Database health
GET /health/database`}</pre>
        </div>

        <h3>Logging Configuration</h3>
        <div className="code-block">
          <pre>{`# PM2 logging
pm2 logs iot-smarthome-backend

# Real-time monitoring
pm2 monit

# Log rotation
pm2 install pm2-logrotate`}</pre>
        </div>

        <h3>Health Check Script</h3>
        <div className="code-block">
          <pre>{`#!/bin/bash
response=$(curl -s -o /dev/null -w "%{http_code}" http://localhost:4008/health)
if [ $response -eq 200 ]; then
    echo "✅ Application is healthy"
else
    echo "❌ Application is unhealthy (HTTP $response)"
    pm2 restart iot-smarthome-backend
fi`}</pre>
        </div>
      </section>

      <section className="content-section">
        <h2>Troubleshooting</h2>
        
        <h3>Common Issues</h3>
        
        <h4>MQTT Connection Issues</h4>
        <div className="code-block">
          <pre>{`# Check MQTT health
curl http://localhost:4008/health/mqtt

# Check PM2 logs for MQTT errors
pm2 logs iot-smarthome-backend | grep -i mqtt`}</pre>
        </div>

        <h4>Certificate Issues</h4>
        <div className="code-block">
          <pre>{`# Verify certificate files exist and have correct permissions
ls -la certs/
openssl x509 -in certs/certificate.pem.crt -text -noout`}</pre>
        </div>

        <h4>Memory Issues</h4>
        <div className="code-block">
          <pre>{`# Monitor memory usage
pm2 monit

# If memory usage is high, restart the application
pm2 restart iot-smarthome-backend`}</pre>
        </div>
      </section>
        </div>
      </div>
    </div>
  );
};
